import { cn } from '@poseidon/components/cn';
import { Arrows, General } from '@poseidon/components/StrokeIcons';

import type { FaceTone } from '~/components/Sequence/sequenceSteps';
import { SEQUENCE_PREP_STEP } from '~/components/Sequence/sequenceSteps';

interface Props {
  /** The four stages are listed beside the node (see {@link SequencePreStepDetail}). */
  expanded: boolean;
  onToggle: () => void;
  /**
   * `true` once the contact is out in the authored steps — prep is history, so the node reads as a
   * completed checkpoint. `false` while they're still in it, and {@link statusLabel} names the stage.
   */
  done: boolean;
  /** The exact stage this contact sits on right now, e.g. "Researching" / "Reconnect LinkedIn" (live only). */
  statusLabel?: string;
  /** Alert tone for that stage: amber = stuck but auto-retrying, red = dead end. */
  tone?: FaceTone | null;
}

/** The pill's border / background / text, by what's happening to THIS contact. */
function prepToneClass(done: boolean, tone: FaceTone | null | undefined): string {
  if (done)
    return 'border-dashed border-lynch-300 bg-slate-100/70 text-lynch-500 hover:border-lynch-400 hover:text-lynch-600 dark:border-slate-600 dark:bg-slate-800/50 dark:text-lynch-400 dark:hover:text-lynch-300';
  if (tone === 'error')
    return 'border-rose-300 bg-rose-50 text-rose-600 dark:border-rose-500/60 dark:bg-rose-500/10 dark:text-rose-300';
  if (tone === 'blocked')
    return 'border-amber-300 bg-amber-50 text-amber-700 dark:border-amber-500/60 dark:bg-amber-500/10 dark:text-amber-300';
  return 'border-blue-300 bg-blue-50 text-blue-600 dark:border-blue-500/60 dark:bg-blue-500/10 dark:text-blue-300';
}

/**
 * The automatic front-half of the journey as ONE node, for the LOCKED per-contact graph: research →
 * dossier → qualify → enroll, folded into "Prep". The builder draws the four stages separately (a rep
 * designing a pipeline reads them one by one, and each stage carries its own pile of waiting people); a
 * contact's own page doesn't — for one person it's a single checkpoint that has either already happened or
 * is happening right now, so it's drawn that way and expands to the four stages on click.
 */
export function SequencePrepNode({ expanded, onToggle, done, statusLabel, tone }: Props) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className={cn(
        'flex h-9 items-center gap-2 whitespace-nowrap rounded-full border px-3 text-xs font-medium transition-colors [&>svg]:size-4 [&>svg]:shrink-0',
        prepToneClass(done, tone),
        // Expansion is marked with a ring rather than a colour swap, so the node keeps saying what's
        // happening to the contact (amber = parked, red = dead end) while its detail is open.
        expanded && 'ring-2 ring-blue-400/40',
      )}>
      {done ? <General.CheckCircle className="text-green-500 dark:text-green-400" /> : SEQUENCE_PREP_STEP.icon}
      {SEQUENCE_PREP_STEP.label}
      {/* Live: the exact stage, so a contact parked mid-prep ("Reconnect LinkedIn") says so on the canvas
          instead of sitting behind a generic node that looks identical to a finished one. */}
      {!done && statusLabel != null && (
        <span className="font-normal opacity-80">
          · <span className="font-medium">{statusLabel}</span>
        </span>
      )}
      <Arrows.ChevronRight className={cn('size-3.5 transition-transform', expanded && 'rotate-90')} />
    </button>
  );
}
